'use client';

import { useCallback, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUser } from '@kit/supabase/hooks/use-user';

import {
  type ToggleFavoriteResult,
  getMyFavoriteCharacterIds,
  toggleFavorite,
} from '~/lib/server/actions/favorite-actions';

export const FAVORITES_QUERY_KEY = ['favorites'] as const;

/**
 * The signed-in user's favourite character ids, shared by every heart on the
 * page through one react-query entry. Toggles are optimistic and roll back
 * on failure. Signed out: empty set, nothing fetched.
 */
export function useFavorites() {
  const { data: user } = useUser();
  const queryClient = useQueryClient();
  const signedIn = !!user;

  const query = useQuery({
    queryKey: FAVORITES_QUERY_KEY,
    queryFn: getMyFavoriteCharacterIds,
    enabled: signedIn,
    staleTime: 5 * 60 * 1000,
  });

  const ids = useMemo(() => new Set(query.data ?? []), [query.data]);

  const toggle = useCallback(
    async (characterId: string): Promise<ToggleFavoriteResult> => {
      const previous =
        queryClient.getQueryData<string[]>(FAVORITES_QUERY_KEY) ?? [];
      const next = previous.includes(characterId)
        ? previous.filter((id) => id !== characterId)
        : [...previous, characterId];

      queryClient.setQueryData(FAVORITES_QUERY_KEY, next);

      const result = await toggleFavorite(characterId);
      if ('error' in result) {
        queryClient.setQueryData(FAVORITES_QUERY_KEY, previous);
      }
      return result;
    },
    [queryClient],
  );

  return {
    signedIn,
    ids,
    isFavorite: (characterId: string) => ids.has(characterId),
    toggle,
    loading: signedIn && query.isLoading,
  };
}
